import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { APP_STATE, ROUTE_MAP } from '../constants';
import { RefreshTokenContext } from '../context';
import { useAppStateContext, useAuthContext } from '../hooks';

const fetchRefreshToken = () => fetch('/refresh-token', { method: 'POST' });

const ONE_MIN_IN_MS = 60 * 1000;
const TEN_MIN_IN_MS = 10 * ONE_MIN_IN_MS;

export const RefreshTokenProvider = ({ children }) => {
  const { appState } = useAppStateContext();
  const { setUserRole } = useAuthContext();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const refreshTokenSubscriptionRef = useRef(null);

  const resetRefreshTokenSubscription = () => {
    if (!refreshTokenSubscriptionRef.current) return;
    clearTimeout(refreshTokenSubscriptionRef.current);
    refreshTokenSubscriptionRef.current = null;
  };

  const handleRefreshTokenSubscription = useCallback(async () => {
    if (appState === APP_STATE.OFFLINE) return;

    try {
      const response = await fetchRefreshToken();

      if (response.status === 401) {
        resetRefreshTokenSubscription();
        setUserRole(null);
        navigateRef.current(ROUTE_MAP.LOGIN);
        toast.info('Failed to refresh token. Automatic logout', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }

      if (response.status !== 200) throw new Error();

      const currentTimeInMs = moment().valueOf();
      const tokenExpiresAtInMs = Cookies.get('tokenExpiresAt');
      const timeUntilExpirationInMs =
        Number(tokenExpiresAtInMs) - currentTimeInMs;
      const refreshTimeInMs = timeUntilExpirationInMs - TEN_MIN_IN_MS;

      refreshTokenSubscriptionRef.current = setTimeout(() => {
        handleRefreshTokenSubscription();
      }, refreshTimeInMs);
    } catch {
      refreshTokenSubscriptionRef.current = setTimeout(() => {
        handleRefreshTokenSubscription();
      }, ONE_MIN_IN_MS);
    }
  }, [setUserRole, appState]);

  useEffect(() => {
    handleRefreshTokenSubscription();

    return resetRefreshTokenSubscription;
  }, [handleRefreshTokenSubscription]);

  return (
    <RefreshTokenContext.Provider value={{}}>
      {children}
    </RefreshTokenContext.Provider>
  );
};
